import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import Breadcrumb from "@/components/feature/Breadcrumb";

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

function formatViews(num: number) {
  if (num >= 10000) return `${(num / 10000).toFixed(1)}万`;
  return num.toString();
}

function ArticleContent({ content }: { content: string }) {
  const paragraphs = content.split("\n\n");
  return (
    <div className="space-y-4">
      {paragraphs.map((para, idx) => {
        if (para.startsWith("**") && para.endsWith("**")) {
          const text = para.slice(2, -2);
          return (
            <h2
              key={idx}
              className="text-lg md:text-xl font-bold text-foreground-950 mt-6 mb-3"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {text}
            </h2>
          );
        }
        if (para.startsWith("*") && para.includes("*")) {
          const parts = para.split("*").filter(Boolean);
          return (
            <h3
              key={idx}
              className="text-base font-bold text-foreground-800 mt-4 mb-2"
            >
              {parts.join("")}
            </h3>
          );
        }
        if (para.startsWith("> ")) {
          return (
            <blockquote
              key={idx}
              className="border-l-4 border-primary-300 pl-4 py-2 my-4 bg-primary-50/50 rounded-r-md"
            >
              <p className="text-sm md:text-base text-foreground-700 italic leading-relaxed">
                {para.slice(2)}
              </p>
            </blockquote>
          );
        }
        return (
          <p
            key={idx}
            className="text-sm md:text-base text-foreground-700 leading-relaxed"
          >
            {para}
          </p>
        );
      })}
    </div>
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.slug === id);

  if (!article) {
    return (
      <main className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 text-center">
        <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
        <p className="text-foreground-500 mb-6">该爆料内容可能已被删除或不存在。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-md text-sm font-bold hover:bg-primary-600 transition-colors"
        >
          返回首页
        </Link>
      </main>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 4);

  return (
    <main>
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: article.title,
          description: article.summary,
          image: article.coverImage,
          datePublished: article.publishedAt,
          dateModified: article.publishedAt,
          author: {
            "@type": "Organization",
            name: article.author,
          },
          publisher: {
            "@type": "Organization",
            name: "爆料网",
            logo: {
              "@type": "ImageObject",
              url: "http://rbecpr.cn/logo.png",
            },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `http://rbecpr.cn/article/${article.slug}`,
          },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "http://rbecpr.cn/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: category?.name || article.category,
              item: `http://rbecpr.cn/category/${article.categorySlug}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.title,
              item: `http://rbecpr.cn/article/${article.slug}`,
            },
          ],
        })}
      </script>

      {/* Article Header */}
      <section className="bg-background-50 pt-20 md:pt-24 pb-8">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              {
                label: category?.name || article.category,
                href: `/category/${article.categorySlug}`,
              },
              { label: article.title },
            ]}
          />

          <div className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              {article.isExclusive && (
                <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-bold">
                  独家首发
                </span>
              )}
              {article.isHot && (
                <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-bold">
                  全网热榜
                </span>
              )}
              <span className="text-xs text-foreground-400 px-2.5 py-1 rounded-md bg-background-100">
                {article.category}
              </span>
            </div>

            <h1
              className="text-2xl md:text-3xl lg:text-4xl font-black text-foreground-950 leading-tight mb-4"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {formatDate(article.publishedAt)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {formatViews(article.views)} 次浏览
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Cover Image */}
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="rounded-lg overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full aspect-video object-cover"
            loading="eager"
          />
        </div>
      </div>

      {/* Article Body */}
      <section className="py-8 md:py-12 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <article className="bg-background-100 rounded-lg p-5 md:p-8">
            {/* Summary */}
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6 pb-6 border-b border-background-200">
              <span className="font-bold text-foreground-800">摘要：</span>
              {article.summary}
            </p>

            <ArticleContent content={article.content} />

            {/* Tags */}
            <div className="mt-8 pt-6 border-t border-background-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-400">标签：</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="px-3 py-1 rounded-full bg-background-200 text-xs text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <h2
              className="text-xl md:text-2xl font-black text-foreground-950 mb-6"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              相关猛料
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  className="group block bg-background-50 rounded-lg overflow-hidden hover:shadow-sm transition-shadow"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-1.5 text-xs text-foreground-400">
                      <span className="text-primary-500 font-medium">
                        {ra.category}
                      </span>
                      <span>·</span>
                      <span>{formatViews(ra.views)}浏览</span>
                    </div>
                    <h3 className="text-sm font-bold text-foreground-950 group-hover:text-primary-500 transition-colors leading-snug line-clamp-2">
                      {ra.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
